export interface ParsedMarkdown {
  metadata: {
    name: string;
    title?: string;
    category?: string;
    description?: string;
    tags?: string[];
    version?: string;
  };
  content: string;
}

/**
 * Parse markdown file with YAML frontmatter
 * Extracts metadata from --- delimited frontmatter and content
 * Handles various frontmatter formats including:
 * - Frontmatter at start of file
 * - Title before frontmatter
 * - Blank lines in frontmatter
 */
export const parseMarkdownWithFrontmatter = (markdown: string): ParsedMarkdown | null => {
  // Handle both formats:
  // 1. Title\n\n---\nfrontmatter\n---\ncontent
  // 2. ---\nfrontmatter\n---\ncontent
  const frontmatterRegex = /(?:^[\s\S]*?)---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = markdown.match(frontmatterRegex);

  if (!match) {
    return null;
  }

  const [, frontmatterText, content] = match;
  const metadata: ParsedMarkdown['metadata'] = { name: '' };

  // Parse YAML frontmatter (simple key-value parsing)
  const lines = frontmatterText.split('\n');
  for (const line of lines) {
    // Skip empty lines
    if (!line.trim()) continue;

    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;

    const key = line.substring(0, colonIndex).trim();
    const value = line.substring(colonIndex + 1).trim();

    if (key === 'name') {
      metadata.name = value;
    } else if (key === 'title') {
      metadata.title = value;
    } else if (key === 'category') {
      metadata.category = value;
    } else if (key === 'description') {
      metadata.description = value;
    } else if (key === 'tags') {
      // Parse comma-separated tags
      metadata.tags = value.split(',').map(tag => tag.trim());
    } else if (key === 'version') {
      metadata.version = value;
    }
  }

  // Validate required name field
  if (!metadata.name) {
    return null;
  }

  return {
    metadata,
    content: content.trim(),
  };
};

/**
 * Read and parse markdown file
 */
export const readMarkdownFile = (file: File): Promise<ParsedMarkdown | null> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseMarkdownWithFrontmatter(text);
      resolve(parsed);
    };

    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`));
    };

    reader.readAsText(file);
  });
};
